/**
 * ===    USAGE    ===
 *
 * var askTechParser = new window.AskTechParser();
 * askTechParser.config({ // See config method documentation below
 *   userInfos: userInfos,
 *   users: users,
 *   cdnUrl: cdnUrl
 * });
 *
 * var htmlText        = askTechParser.mdToHtml(markdownText, plain);
 * var displayableText = askTechParser.htmlToDisplayable(htmlText, plain);
 *
 *
 *
 * === EXPLANATIONS ===
 *
 * plain: Boolean that converts formated text into plain text
 * markdownText and htmlText should never be added to the document. Only displayableText should be displayed.
 *
 *
 *
 * ===    NOTES     ===
 *
 * Do not use async functions, required babel polyfill won't be available for this file
 *
 */

;(function() {

class AskTechParser {

  constructor() {
    this.Emoji = new Emoji();
    this.LightMarkdown = new LightMarkdown();
    this._parserCache = new ParserCache();
    this._customParsers = [];
    this.config({});
  }

  /**
   * Use `userInfos: 'DUMMY'` to load a sample "John Doe" profile.
   * @param  {object} data {
   *                         userInfos: { display_name: String, more: {} },
   *                         users: [{ id: Number, image: String, name: String, uid: String }],
   *                         cdnUrl: String,
   *                         drawImages: boolean,        // Default: false, should be handled by the preview generator
   *                         mentionPrefix: String,      // Default: null, prefix will be the user's avatar
   *                         cacheEnabled: boolean,      // Default: false, cache similar functions call
   *                         parseVariables: boolean,
   *                         pageHostname: String,       // Hostname of the page hosting the widget
   *                         pageHref: String            // Full href of the page hosting the widget (base for relative URLs)
   *                       }
   */
  config(data) {
    this._userInfos           = Utils.firstDefined([data.userInfos,           this._userInfos]);
    this._users               = Utils.firstDefined([data.users,               this._users]);
    this._cdnUrl              = Utils.firstDefined([data.cdnUrl,              this._cdnUrl]);
    this._drawImages          = Utils.firstDefined([data.drawImages,          this._drawImages,           false]);
    this._mentionPrefix       = Utils.firstDefined([data.mentionPrefix,       this._mentionPrefix,        null]);
    this._parseVariables      = Utils.firstDefined([data.parseVariables,      this._parseVariables,       true]);
    this._translationTool     = Utils.firstDefined([data.translationTool,     this._translationTool,      null]);
    this._piiTokens           = Utils.firstDefined([data.piiTokens,           this._piiTokens,            []]);
    this._pageHostname        = Utils.firstDefined([data.pageHostname,        this._pageHostname,         null]);
    this._pageHref            = Utils.firstDefined([data.pageHref,            this._pageHref,             null]);

    Utils._pageHostname = this._pageHostname;
    Utils._pageHref     = this._pageHref;

    if (this._userInfos === 'DUMMY') {
      this._userInfos = {
        display_name: 'John Doe',
        more: {
          email: 'john.doe@example.com',
          firstname: 'John',
          lastname: 'Doe'
        }
      }
    }

    this.Emoji.config(data);
    this.LightMarkdown.config(data);
    this._parserCache.config(data);

    this.overridenVariables = [
      { variable: 'generating', wordingKey: 'ai_generating' },
      { variable: 'searching', wordingKey: 'ai_searching' },
      { variable: 'moderated', wordingKey: 'content_moderated' },
      { variable: 'moderated_reply', wordingKey: 'content_moderated_reply' },
    ]
  }

  _checkConfig() {
    if (!(this._userInfos && this._users && this._cdnUrl)) {
      throw "AskTechParser is not properly configured";
    }
  }

  /**
   * Adds a custom parser that is applied to the Displayable output
   * USE ONLY IF ADDITIONAL SPECIFIC PROCESSING IS NEEDED,
   * PLEASE EDIT THIS FILE IF YOU NEED THE BEHAVIOR TO BE FIXED GLOBALLY
   * @param {function} parser (displayable, plain), should return the parsed displayable
   */
  addCustomParser(customParser) {
    this._customParsers.push(customParser);
  }


  /* PARSERS */

  textToHtml(text, plain = false) {
    this._checkConfig();

    return this._parserCache.sync('textToHtml' + (plain ? '_plain' : ''), text, () => {
      let           html = text;
      if (!plain)   html = this.parseLinksInMessage(html);
                    html = this.parseMessageMailto(html);
                    html = this.LightMarkdown.toHtml(html, plain, 'text');
      return        html;
    });
  }

  mdToHtml(markdown, plain = false) {
    this._checkConfig();

    return this._parserCache.sync('mdToHtml' + (plain ? '_plain' : ''), markdown, () => {
      let takeaways = [];
      let           html = this.prepareAiMarkers(markdown, takeaways);
      if (!plain)   html = this.parseLinksInMessage(html);
                    html = this.parseMessageMailto(html);
                    html = this.LightMarkdown.toHtml(html, plain);
                    html = this.restoreAiTakeaways(html, takeaways, plain);
                    html = this.parseMessageAtUsers(html);
      return        html;
    });
  }

  htmlToDisplayable(html, plain = false) {
    this._checkConfig();

    return this._parserCache.sync('htmlToDisplayable' + (plain ? '_plain' : ''), html, () => {
      let         displayable = html;
                  displayable = this.parsePiiTokens(displayable);
                  displayable = this.parseMessageVariables(displayable);
                  displayable = this.parseYoutubeImageButton(displayable);
                  displayable = this.parseEmoji(displayable, plain);
      let           parsedDOM = new ParsedDOM(displayable);
                                this.removeJavaScript(parsedDOM);
      parsedDOM.body.querySelectorAll('a[href]').forEach((a) => {
        if (!a.getAttribute('target')) {
          a.setAttribute('target', Utils.linkTarget(a.getAttribute('href')));
        }
      });
      if (plain)  displayable = this.parsePlain(parsedDOM);
      else        displayable = parsedDOM.body.innerHTML;
      this._customParsers.forEach(customPraser => {
                  displayable = customPraser(displayable, plain);
      });
      return      displayable;
    });
  }

  textToDisplayable(text, plain = false) {
    return this.htmlToDisplayable(this.textToHtml(text, plain), plain);
  }

  mdToDisplayable(markdown, plain = false) {
    return this.htmlToDisplayable(this.mdToHtml(markdown, plain), plain);
  }


  /* SUB-PARSERS */

  /**
   * Strips AI [cite] markers (keeps inner text) and extracts [takeaway] blocks
   * into placeholders restored after markdown as <div class="slaask-ai-takeaway">.
   */
  prepareAiMarkers(content, takeaways) {
    if (!content || typeof content !== 'string') return content || '';

    content = content
      .replace(/\[\s*cite:/gi, '[cite:')
      .replace(/\[\s*\/\s*cite\s*\]/gi, '[/cite]');

    content = content.replace(/\[cite:[^\]]+\]([\s\S]*?)\[\/cite\]/gi, '$1');
    content = content.replace(/\[cite:[^\]]*\]?/gi, '');
    content = content.replace(/\[\/cite\]/gi, '');
    content = content.replace(/\[ref:[^\s\]]+\]/gi, '');

    content = content.replace(/\[takeaway\]([\s\S]*?)\[\/takeaway\]/gi, (_, inner) => {
      const index = takeaways.push(inner.trim()) - 1;
      return '₪₪TAkEaWaY₪₪' + index + '₪₪';
    });

    const incompleteIndex = content.search(/\[takeaway\]/i);
    if (incompleteIndex !== -1) {
      content = content.slice(0, incompleteIndex);
    }

    return content;
  }

  restoreAiTakeaways(html, takeaways, plain = false) {
    if (!takeaways || takeaways.length === 0) return html;

    takeaways.forEach((raw, index) => {
      let inner = raw;
      if (!plain) inner = this.parseLinksInMessage(inner);
      inner = this.parseMessageMailto(inner);
      inner = this.LightMarkdown.toHtml(inner, plain);

      const replacement = plain
        ? inner
        : '<div class="slaask-ai-takeaway">' + inner + '</div>';

      const token = '₪₪TAkEaWaY₪₪' + index + '₪₪';
      const wrappedRe = new RegExp('<p>\\s*' + token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*<\\/p>', 'g');
      const withWrapped = html.replace(wrappedRe, replacement);
      html = withWrapped !== html ? withWrapped : html.replace(token, replacement);
    });

    return html;
  }

  /**
   * @param  {ParsedDOM|String} html
   * @param  {String} lineBreakDelimiter Character used for line breaks from <br> and <p> elements
   * @return {String}
   */
  parsePlain(html, lineBreakDelimiter = " ") {
    let parsedDOM = typeof html == 'string'
                    ? new ParsedDOM(html)
                    : html;
    return parsedDOM.getTextContent(lineBreakDelimiter) || "";
  }

  /**
   * Converts non-formated links into markdown
   * Image links are converted into ![alt](url)
   * @param  {String} message
   * @return {String}
   */
  parseLinksInMessage(message) {
    message = message.replace(/(\s|^)(https?:\/\/\S+)/gi, "$1<$2>")
                     .replace(/<?(https?:\/\/.*?\.(?:png|jpg|gif|svg|jpeg))(\?([^\s]*?))?(\|(.*?))>?(\s|$)/gi, '![$5]($1$2)')
                     .replace(/<?(https?:\/\/.*\.(?:png|jpg|gif|svg|jpeg))(\?([^\s]*?))?>?(\s|$)/gi, '![attachment]($1$2)$4')

    return message
  }

  parseEmoji(html, plain = false) {
    if (plain)
      return this.Emoji.emojify(html, false);
    else
      return this.Emoji.emojify(html);
  }

  parseMessageMailto(text) {
    let reg = /[a-z0-9_\-+=.]+@[a-z0-9\-]+(\.[a-z0-9-]+)\|[a-z0-9_\-+=.]+@[a-z0-9\-]+(\.[a-z0-9-]+)/ig
    return text.replace(reg, (match) => {
      return match.substring(0, match.indexOf('|'));
    });
  }

  parseMessageVariables(text) {
    if (this._parseVariables) {
      let findVariable = (space, variable, fallbackText) => {
        variable = variable.trim();
        let value = !fallbackText || fallbackText == 'null' ? '' : fallbackText;
        if (this._userInfos) {
          if (variable == 'name' && this._userInfos['display_name'])
            value = this._userInfos['display_name'].split('-#')[0];

          else if (this._userInfos['more'] && typeof this._userInfos['more'][variable] != 'undefined')
            value = this._userInfos['more'][variable];
        }

        return value == '' ? '' : space + value;
      };

      text = text.replace(/(\s?)({{)(\S*?)(?:\|\|(\S*?))?(}})/ig, (match, mSpace, mStart, mVariable, mFallback, mEnd) => {
        const matchedOverridenVariables = this.overridenVariables.find(variable => variable.variable === mVariable);

        if (matchedOverridenVariables) {
          if (this._translationTool) {
            return this._translationTool.t(`widget.${matchedOverridenVariables.wordingKey}`);
          } else {
            return matchedOverridenVariables.wordingKey;
          }
        } else {
          return findVariable(mSpace, mVariable, mFallback);
        }
      });

      text = text.replace(/(\s?)<span class="variable"(.*?)<\/span>/ig, (match, mSpace, mData) => {
        let variable = mData.match(/data-variable(?:-id)?="(.*?)"/)[1];
        let fallbackMatch = mData.match(/data-placeholder="(.*?)"/);
        let fallback = fallbackMatch ? fallbackMatch[1] : '';
        return findVariable(mSpace, variable, fallback);
      });
    }

    return text;
  }

  parsePiiTokens(text) {
    // If nothing to do, return original text
    if (!text) return text;

    const masked = '<span style="opacity: 0.5;">anonymisé</span>';

    // Sort tokens descending by length for no partial replacement issues
    let tokens = (this._piiTokens || []).slice().sort((a, b) => b.length - a.length);

    tokens.forEach(token => {
      // Escape regex chars, downcase for the regex
      let safeToken = token.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match full placeholder tags: <PRIVATE_PERSON> or &lt;PRIVATE_PERSON&gt; (with optional _N suffix)
      let wrappedRegex = new RegExp(`(?:&lt;|<)${safeToken}(?:_\\d+)?(?:&gt;|>)`, 'gi');
      text = text.replace(wrappedRegex, masked);

      // Fallback for bare token occurrences
      let bareRegex = new RegExp(safeToken, 'gi');
      text = text.replace(bareRegex, masked);
    });

    return text;
  }

  parseYoutubeImageButton(text) {
    text = text.replace(/<img src="(.*?)" class="image-preview youtube-preview">/gi, function(img) {
      return `<div class="youtube-preview-container"><button class="ytp-large-play-button ytp-button" aria-label="Play"><svg height="100%" version="1.1" viewBox="0 0 68 48" width="100%"><path class="ytp-large-play-button-bg" d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#212121" fill-opacity="0.8"></path><path d="M 45,24 27,14 27,34" fill="#fff"></path></svg></button>${img}</div>`
    });

    return text;
  }

  parseMessageAtUsers(text) {
    let reg = /&lt;\@\S*&gt;/ig
    return text.replace(reg, (match) => {
      match = match.replace('&lt;', '');
      match = match.replace('&gt;', '');
      match = match.replace('@', '');

      var user = this._users.filter((x) => { return x['uid'] === match; })

      if (user.length == 0) {
        return ''
      } else {
        let prefix = this._mentionPrefix
                      ? this._mentionPrefix
                      : `<img class="slaask-user-small-image" width="16px" height="16px" src="${user[0]['image']}"/>&nbsp;`;

        return `<span class="mention" data-mention-id="${user[0]['uid']}">${prefix}${user[0]['name']}</span>`;
      }
    });
  }

  isOnlyImg (html) {
    let onlyChild = this._getOnlyChild(html);
    return onlyChild && onlyChild.nodeName == 'IMG';
  }

  isOnlyLink (html) {
    let onlyChild = this._getOnlyChild(html);
    return onlyChild && onlyChild.nodeName == 'A';
  }

  /**
   * This function will select a parsing function according message.parser,
   * or guessing with other properties if the parser is not specified.
   * @param  {Message} message A Message object from the database
   * @return {String}          The function name (ex: 'mdToDisplayable')
   */
  selectParsingFunction (message) {
    let parser = message.parser;
    if (parser == null) {
      if (message.message_type == "faq") parser = AskTechParser.parser['html'];
      else parser = AskTechParser.parser['markdown'];
    }

    let parsingFunction;
    if      (parser == AskTechParser.parser['html'])     parsingFunction = 'htmlToDisplayable';
    else if (parser == AskTechParser.parser['markdown']) parsingFunction = 'mdToDisplayable';
    else if (parser == AskTechParser.parser['text'])     parsingFunction = 'textToDisplayable';
    else throw "Unkown parser " + parser;

    return parsingFunction;
  }

  removeJavaScript (parsedDOM) {
    let scripts = parsedDOM.body.querySelectorAll('script, iframe');
    for (let i = 0; i < scripts.length; i += 1) {
      if (scripts[i].parentElement) scripts[i].parentElement.removeChild(scripts[i]);
    }
  }

  _getParsedDom (html) {
    return this._parserCache.sync('_getParsedDom', html, () => {
      return new ParsedDOM(html);
    });
  }

  _getOnlyChild (html) {
    return this._parserCache.sync('_getOnlyChild', html, () => {
      return this._getParsedDom(html).getOnlyChild();
    });
  }
}

class LightMarkdown {

  constructor() {
    this._tokens                  = this._getTokens();
    this._slaaskRegex             = this._getRegex(this._tokens),
    this._plainToken              = '₪₪PLaiN₪₪',
    this._slaaskMarkdownOptions   = {},
    this._flavors = {
      slack: {
        bold: true,
        doublebold: true,
        italics: true,
        strikethrough: true,
        pre: true,
        code: true,
        longQuote: true,
        quote: true,
        autoLink: true,
        autoEmail: true,
        autoImage: true,
        paragraph: true,
        lineBreaks: true
      },
      skype: {
        bold: true,
        doublebold: true,
        italics: true,
        strikethrough: true,
        pre: false,
        code: false,
        longQuote: false,
        quote: false,
        autoLink: true,
        paragraph: false,
        lineBreaks: true
      },
      text: {
        bold: false,
        doublebold: false,
        italics: false,
        strikethrough: false,
        pre: false,
        code: false,
        longQuote: false,
        quote: false,
        autoLink: true,
        autoEmail: true,
        autoImage: true,
        paragraph: false,
        lineBreaks: true
      }
    };

    this.setFlavor('slack');
  }

  config(data) {
    this._drawImages = data.drawImages || this._drawImages || false;
  }


  /**
   * Set a global option
   * @static
   * @param {string} key
   * @param {*} value
   * @returns {lightMarkdown}
   */
  setOption(key, value) {
    this._slaaskMarkdownOptions[key] = !!value;
    return this;
  };

  /**
   * Get a global option
   * @static
   * @param {string} key
   * @returns {*}
   */
  getOption(key) {
    return this._slaaskMarkdownOptions[key];
  };


  setFlavor(flavorName) {
    var flavor = this._flavors[flavorName];
    if (flavor) {
      for (var option in flavor) {
        if (flavor.hasOwnProperty(option)) {
          this._slaaskMarkdownOptions[option] = flavor[option];
        }
      }
    }
    return this;
  };


  toHtml(md, noEmbedly, forcedFlavor) {
    md = this._escapeHtml(md);
    var plains = [];

    var markdownOptions;
    if (forcedFlavor) {
      let flavor = this._flavors[forcedFlavor];
      if (!flavor) throw "Unknown flavor " + forcedFlavor;
      markdownOptions = flavor;
    }
    else markdownOptions = this._slaaskMarkdownOptions;

    // Replace all tokens
    this._tokens.forEach((t) => {
      if (markdownOptions[t.name]) {
        md = md.replace(t.regex, (match, g1, g2) => {
          if (!g2 ||
            t.requireNonTokens && !this._slaaskRegex.nonTokensChars.test(g2) ||
            (t.token.length === 1 && (g2[0] === t.token || g2.slice(-1) === t.token)) ||
            t.spaceWrapIgnored && g2[0] === ' ' && g2.slice(-1) === ' ')
            return match;

           if (typeof t.processContent === 'function') {
            g2 = t.processContent(g2);
          }
          if (t.plainContent) {
            var plainIndex = plains.push(g2) - 1;
            g2 = this._plainToken + plainIndex;
          }
          return g1 + '<' + t.elementName + '>' + g2 + '</' + t.elementName + '>';
        });
      }
    });

    if (markdownOptions.longQuote) {
      md = md.replace(this._slaaskRegex.multilineQuote, (match, g1, g2) => {
        if (match === '&gt;&gt;&gt;')
          return match;

        // trim start unless there are more '>' ahead
        g2 = g2.replace(/^([\s]*)(&gt;)*/, (m, gg1, gg2) => {
          return gg2 ? m : '';
        });
        return '<pre>' + g2 + '</pre>';
      });
    }

    if (markdownOptions.quote) {
      md = md.replace(this._slaaskRegex.singleLineQuote, (match, g1, g2) => {
        if (match === '&gt;')
          return match;
        g2 = g2.replace(/\n&gt;/g, '\n');
        return '<pre>' + g2 + '</pre>';
      });
    }

    // Process images before links to ensure # in image URLs are protected in HTML attributes
    if (markdownOptions.autoImage) {
      // Replace links
      md = md.replace(this._slaaskRegex.image, (match, g1, g2, g3, g4, g5) => {

        // Dropbox
        if (~g4.indexOf('dropbox.com/s/')) {
          if (g4.match('dl=0')) {
            g4 = g4.replace('dl=0', 'raw=1')
          } else {
            g4 = g4 + '?raw=1'
          }
        }

        if (this._drawImages) {
          return '<a href="' + g4 + '"' + Utils.linkTargetAttr(g4) + '><img src="' + g4 + '" alt=' + g2 + '/></a>';
        }
        else {
          return '<a href="' + g4 + '"' + Utils.linkTargetAttr(g4) + '>' + g2 + '</a>';
        }
      });
    }

    if (markdownOptions.autoLink) {
      md = md.replace(this._slaaskRegex.url, (match, g1, g2) => {
        match = match.replace('&lt;', '');
        match = match.replace('&gt;', '');

        let linkToReturn = null

        // GOOGLE DOCS

        if (match.match('docs.google.com')) {
          linkToReturn = '<a href="' + match + '"' + Utils.linkTargetAttr(match) + ' class="">' + match + '</a>';
        }

        // GIPHY

        if (match.match('giphy.com')) {
          linkToReturn = '<img class="slaask-message-gif" src="' + match + '" />'
        }

        let url, text;
        if (match.indexOf('|') != -1 && match.substring(match.indexOf('|') + 1, match.length) != '') {
          url = match.substring(0, match.indexOf('|'))
          text = match.substring(match.indexOf('|') + 1, match.length)
        } else {
          url = text = match
        }
        linkToReturn = '<a href="' + url + '"' + Utils.linkTargetAttr(url) + ' class="" data-link="true">' + text + '</a>';

        if(match.substr(0, 1) == '!') {
          linkToReturn = linkToReturn.replace('class="', 'class="slaask-btn ').replace('!http', 'http').replace('_blank', '_self')
        }

        return linkToReturn
      });

      md = md.replace(this._slaaskRegex.mdUrl, (match, g1, g2, g3, g4, g5) => {
        if (g1 && g1.startsWith('BUTTON: ')) {
          const btnLabel = g1.replace(/^BUTTON: /, '');
          return '<button type="button" class="slaask-btn slaask-btn-non-centered" onclick="window.open(\'' + g2 + '\', \'_blank\');">' + btnLabel + '</button>';
        }
        return '<a href="' + g2 + '"' + Utils.linkTargetAttr(g2) + '>' + g1 + '</a>';
      });
    }

    if (markdownOptions.autoEmail) {
      // Replace links
      md = md.replace(this._slaaskRegex.email, (match, g1, g2) => {
        match = match.replace('&lt;', '');
        match = match.replace('&gt;', '');
        return '<a href="' + match + '"' + Utils.linkTargetAttr(match) + '>' + match.replace('mailto:', '') + '</a>';
      });
    }

    if (markdownOptions.paragraph) {
      // Protect # in HTML attributes (like href="url#anchor") before parsing headers
      var hashPlaceholders = [];
      var hashPlaceholderIndex = 0;
      md = md.replace(/(href|src|action|data-[^=]*)=(["'])([^"']*?)(#)([^"']*?)(\2)/gi, function(match, attr, quote1, beforeHash, hash, afterHash, quote2) {
        var placeholder = '___HASH_PLACEHOLDER_' + hashPlaceholderIndex + '___';
        hashPlaceholders.push({ placeholder: placeholder, hash: hash });
        hashPlaceholderIndex++;
        return attr + '=' + quote1 + beforeHash + placeholder + afterHash + quote2;
      });

      [
        'h4',
        'h3',
        'h2',
        'h1'
      ].forEach((h) => {
        md = md.replace(this._slaaskRegex[h], (match, g1, g2) => {
          // g1 is the line start/newline, g2 is the header content
          return g1 + '<span class="slaask-message-header-' + h + '">' + g2 + '</span>';
        });
      });

      // Restore # in HTML attributes
      hashPlaceholders.forEach(function(item) {
        md = md.replace(item.placeholder, item.hash);
      });

      // Create paragraphs
      var m;
      var doubleLineIndexes = [];
      while ((m = this._slaaskRegex.doubleLineBreak.exec(md))) {
        doubleLineIndexes.push({
          start: m.index,
          length: m[0].length
        });
      }
      while ((m = this._slaaskRegex.blockquoteTags.exec(md))) {
        doubleLineIndexes.push({
          start: m.index,
          length: m[0].length,
          suffix: m[0]
        });
      }
      doubleLineIndexes.push({
        start: md.length,
        length: 0
      });

      doubleLineIndexes.sort((a, b) => {
        return a.start - b.start;
      });

      var withParagraphs = '';
      var startIndex = 0;
      doubleLineIndexes.forEach((doubleLine) => {
        var paragraph = '';
        var paragraphContent = md.substring(startIndex, doubleLine.start);
        if (paragraphContent) {
          paragraph = '<p>' + paragraphContent + '</p>';
        }
        if (doubleLine.suffix) {
          paragraph += doubleLine.suffix;
        }
        withParagraphs += paragraph;
        startIndex = doubleLine.start + doubleLine.length;
      });
      md = withParagraphs;
    }

    if (markdownOptions.lineBreaks) {
      // Create line breaks
      md = md.replace(this._slaaskRegex.singleLineBreak, '<br/>');
    }
    // Restore plain parts
    plains.forEach((p, plainIndex) => {
      md = md.replace(this._plainToken + plainIndex, p);
    });

    return md;
  };


  _escapeHtml(content) {
    if (content) {
      content = content.replace(/>/g, '&gt;');
      content = content.replace(/</g, '&lt;');
    }
    return content;
  }

  _getRegex(tokens) {
    var allTokenChars = tokens.map((t) => {
      return t.token.length === 1 ? t.token : '';
    }).join();

    var nonTokensCharsRegex = new RegExp('[^' + allTokenChars + ']');

    // Multiline quote >>>
    var multilineQuoteRegex = /(^|\n)&gt;&gt;&gt;([\s\S]*$)/;

    // Single line quote > or sequential lines that start with >.
    var singleLineQuoteRegex = /(^|\n)&gt;(([^\n]*)(\n&gt;[^\n]*)*)/g;

    // Two new lines in a row
    var doubleLineBreakRegex = /\r?\n\r?\n\r?/g;

    // Single line break
    var singleLineBreakRegex = /\r?\n\r?/g;

    // var urlRegex = /(&lt;https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w\_\&\?\=\%\.\-]*)(\|)?([\S]+)?((&gt)?;)?/gi;
    var urlRegex = /(&lt;!?https?:\/\/(.*?)(|)?(.*?)?&gt;)/gi;

    var mdUrlRegex = /\[([^\]]+)\]\(((?:https?|tel|mailto)[^)]+)\)/gi;

    // Headers regex: only match # at start of line or after newline, not in HTML attributes
    var h1Regex = /(^|\n)\#{1}([^\n<]*)/gi;

    var h2Regex = /(^|\n)\#{2}([^\n<]*)/gi;

    var h3Regex = /(^|\n)\#{3}([^\n<]*)/gi;

    var h4Regex = /(^|\n)\#{4}([^\n<]*)/gi;

    var emailRegex = /(&lt;mailto:)((([\da-z+\.-]+)\@([a-z0-9\.]{2,6})([\/\w \.-]*)(\|)?([\da-z\.-]+)?\.?([a-z\.]{2,6})?)|(\{\{[\w]+\}\}))(&gt;)?/gi;

    var imageRegex = /(!\[)(.*?)(\]\()(https?:\/\/(.*?))(\))/gi;

    return {
      nonTokensChars: nonTokensCharsRegex,
      multilineQuote: multilineQuoteRegex,
      singleLineQuote: singleLineQuoteRegex,
      blockquoteTags: /<\/?blockquote>/ig,
      doubleLineBreak: doubleLineBreakRegex,
      singleLineBreak: singleLineBreakRegex,
      url: urlRegex,
      mdUrl: mdUrlRegex,
      email: emailRegex,
      image: imageRegex,
      h1: h1Regex,
      h2: h2Regex,
      h3: h3Regex,
      h4: h4Regex
    };
  }

  // From http://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
  _escapeRegExp(str) {
    return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
  }

  _getTokens() {
    var tokens = [
      {name: 'pre', token: '```', elementName: 'pre', multiline: true, plainContent: true},
      {name: 'code', token: '`', elementName: 'code', ignoreAfter: true, plainContent: true},
      {name: 'bold', token: '*', elementName: 'b', requireNonTokens: true, spaceWrapIgnored: true},
      {name: 'doublebold', token: '**', elementName: 'b', requireNonTokens: true, spaceWrapIgnored: true},
      {name: 'italics', token: '_', elementName: 'em', requireNonTokens: true},
      {name: 'strikethrough', token: '~', elementName: 's', requireNonTokens: true, spaceWrapIgnored: true}
    ];
    tokens.forEach((t) => {
      if (!t.regex) {
        var before = '(^|[\\s\\?\\.,\\\'\\-!\\^;:{(\\[%$#+="])';
        var content = t.multiline ? '([\\s\\S]*?)?' : '(.*?\\S *)?';
        var after = t.ignoreAfter ? '' : '(?=$|\\s|[\\?\\.,\'\\-!\\^;:})\\]%$~{\\[<#+="])';
        var token = this._escapeRegExp(t.token);
        var pattern = before + token + content + token + after;
        t.regex = new RegExp(pattern, 'g');
      }
    });
    return tokens;
  }


};

class Emoji  {

  constructor() {
    this._initLists();
    this._parserCache = new ParserCache();
  }

  config(data) {
    this._cdnUrl = data.cdnUrl || this._cdnUrl;

    this._parserCache.config(data);
  }

  isOnlyEmoji(str) {
    return this._parserCache.sync('isOnlyEmoji', str, () => {
      return this.isOnlyEmojiCode(str) || this.isOnlyEmojiHtml(str);
    });
  }

  isOnlyEmojiCode(str) {
    let match = str.match(/\:([a-z0-9_+-]+)(?:\[((?:[^\]]|\][^:])*\]?)\])?\:/g);
    if (match != null && str == match[0]) {
      return true;
    } else {
      // replace(...) contains an invisible character. Don't remove it :D
      let strWithoutEmojis = str.replace(Emoji.regex.native, '').trim().replace("️", '');

      return strWithoutEmojis == '' || strWithoutEmojis == '<p></p>';
    }
  }

  isOnlyEmojiHtml(html) {
    let parsedDOM = new ParsedDOM(html);
    let onlyChild = parsedDOM.getOnlyChild();
    return onlyChild && Emoji.regex.class.allSizes.test(onlyChild.className);
  }

  // Emojify visitor messages
  // Converts <3 to :heart:
  preEmojify(text) {
    for (var emoticon in this.emoticonsList) {
      if (text.trim() == emoticon) {
        text = ':' + this.emoticonsList[emoticon] + ':';
      } else {
        let regex = new RegExp('( |^)' + emoticon.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g')
        text = text.replace(regex, '$1:' + this.emoticonsList[emoticon] + ':');
      }
    }

    return text;
  }

  // Emojify Agent messages
  emojify(text, allowBig = true) {
    return this.parser(text, this._cdnUrl + '/emoji', { 'classes': 'slaask-emoji', 'allow-big': allowBig });
  }

  parser(text, url, options) {
    var classes, list;
    if (options == null) {
      options = {};
    }
    list = options.list || this.list;
    if (typeof options === 'function') {
      options = {
        parser: options
      };
    } else {
      if (options.attributes == null) {
        options.attributes = {
          title: function(match, name, parameter) {
            if (parameter != null) {
              return "" + parameter + " (" + name + ")";
            } else {
              return name;
            }
          },
          alt: function(match) {
            return match;
          }
        };
      }
      if (options.parser == null) {
        options.parser = function(match, url, classes, options) {
          var key, res, val, _ref;
          res = "<img class=\"" + classes + "\" src=\"" + url + "/" + (encodeURIComponent(match[1])) + ".png\" ";
          _ref = options.attributes;
          for (key in _ref) {
            if ({}.hasOwnProperty.call(_ref, key)) {
              val = _ref[key];
              res += "" + key + "=\"" + (val.apply(options, match)) + "\" ";
            }
          }
          return res + '/>';
        };
      }
    }
    classes = options.classes != null ? options.classes : 'emoji';

    if (text) {
      let parameterRegex  = `(?:\\[((?:[^\\]]|\\][^:])*\\]?)\\])?`;
      let emojiRegex      = `\\:([a-z0-9_+-]+)${parameterRegex}\\:`;
      let emojiOrAltRegex = `(${emojiRegex})|(=\\s?"${emojiRegex}")`;

      text = text.replace(new RegExp(emojiOrAltRegex, 'g'), function(match, code, name, parameter, codeAlt, nameAlt, parameterAlt) {
        var current_class = classes
        var result;

        if (codeAlt) {
          result = codeAlt;
        }
        else {
          if (list.indexOf(name) === -1) {
            if (code.indexOf('skin-tone') === -1) {
              result = code;
            }
            else result = '';
          } else {
            if (options['allow-big'] && text == '<p>' + code + '</p>') {
              current_class = current_class + '-big'
            }
            result = options.parser([code, name, parameter], url, current_class, options);
          }
        }

        return result;
      });

      if (options['allow-big']) {
        let parsedDOM = new ParsedDOM(text);
        let onlyChild = parsedDOM.getOnlyChild();
        if (onlyChild) {
          onlyChild.className = onlyChild.className.replace(Emoji.regex.class.standard, "$1-big");
        }
        text = parsedDOM.body.innerHTML;
      }

      return text;

    } else {
      return text
    }
  }

  _initLists() {
    this.emoticonsList = {
      '<3':'heart',
      ':o)':'monkey_face',
      ':*':'kiss',
      ':-*':'kiss',
      '<\/3':'broken_heart',
      ':slightly_smiling_face:':'simple_smile',
      '=)':'simple_smile',
      '=-)':'simple_smile',
      'C:':'simple_smile',
      'c:':'simple_smile',
      ':D':'smile',
      ':-D':'smile',
      ':>':'laughing',
      ':->':'laughing',
      ';)':'wink',
      ';-)':'wink',
      ':)':'simple_smile',
      '(:':'simple_smile',
      ':-)':'simple_smile',
      '8)':'sunglasses',
      ':|':'neutral_face',
      ':-|':'neutral_face',
      ':\\':'confused',
      ':-\\':'confused',
      ':\/':'confused',
      ':-\/':'confused',
      ':p':'stuck_out_tongue',
      ':-p':'stuck_out_tongue',
      ':P':'stuck_out_tongue',
      ':-P':'stuck_out_tongue',
      ':b':'stuck_out_tongue',
      ':-b':'stuck_out_tongue',
      ';p':'stuck_out_tongue_winking_eye',
      ';-p':'stuck_out_tongue_winking_eye',
      ';b':'stuck_out_tongue_winking_eye',
      ';-b':'stuck_out_tongue_winking_eye',
      ';P':'stuck_out_tongue_winking_eye',
      ';-P':'stuck_out_tongue_winking_eye',
      '):':'disappointed',
      ':(':'disappointed',
      ':-(':'disappointed',
      '>:(':'angry',
      '>:-(':'angry',
      ':\'(':'cry',
      'D:':'anguished',
      ':o':'open_mouth',
      ':-o':'open_mouth'
    };

    this.list = ["+1", "-1", "100", "1234", "8ball", "a", "ab", "abc", "abcd", "accept", "aerial_tramway", "airplane", "alarm_clock", "alien", "ambulance", "anchor", "angel", "anger", "angry", "anguished", "ant", "apple", "aquarius", "aries", "arrow_backward", "arrow_double_down", "arrow_double_up", "arrow_down", "arrow_down_small", "arrow_forward", "arrow_heading_down", "arrow_heading_up", "arrow_left", "arrow_lower_left", "arrow_lower_right", "arrow_right", "arrow_right_hook", "arrow_up", "arrow_up_down", "arrow_up_small", "arrow_upper_left", "arrow_upper_right", "arrows_clockwise", "arrows_counterclockwise", "art", "articulated_lorry", "astonished", "atm", "b", "baby", "baby_bottle", "baby_chick", "baby_symbol", "baggage_claim", "balloon", "ballot_box_with_check", "bamboo", "banana", "bangbang", "bank", "bar_chart", "barber", "baseball", "basketball", "bath", "bathtub", "battery", "bear", "bee", "beer", "beers", "beetle", "beginner", "bell", "bento", "bicyclist", "bike", "bikini", "bird", "birthday", "black_circle", "black_joker", "black_nib", "black_square", "black_square_button", "blossom", "blowfish", "blue_book", "blue_car", "blue_heart", "blush", "boar", "boat", "bomb", "book", "bookmark", "bookmark_tabs", "books", "boom", "boot", "bouquet", "bow", "bowling", "bowtie", "boy", "bread", "bride_with_veil", "bridge_at_night", "briefcase", "broken_heart", "bug", "bulb", "bullettrain_front", "bullettrain_side", "bus", "busstop", "bust_in_silhouette", "busts_in_silhouette", "cactus", "cake", "calendar", "calling", "camel", "camera", "cancer", "candy", "capital_abcd", "capricorn", "car", "card_index", "carousel_horse", "cat", "cat2", "cd", "chart", "chart_with_downwards_trend", "chart_with_upwards_trend", "checkered_flag", "cherries", "cherry_blossom", "chestnut", "chicken", "children_crossing", "chocolate_bar", "christmas_tree", "church", "cinema", "circus_tent", "city_sunrise", "city_sunset", "cl", "clap", "clapper", "clipboard", "clock1", "clock10", "clock1030", "clock11", "clock1130", "clock12", "clock1230", "clock130", "clock2", "clock230", "clock3", "clock330", "clock4", "clock430", "clock5", "clock530", "clock6", "clock630", "clock7", "clock730", "clock8", "clock830", "clock9", "clock930", "closed_book", "closed_lock_with_key", "closed_umbrella", "cloud", "clubs", "cn", "cocktail", "coffee", "cold_sweat", "collision", "computer", "confetti_ball", "confounded", "confused", "congratulations", "construction", "construction_worker", "convenience_store", "cookie", "cool", "cop", "copyright", "corn", "couple", "couple_with_heart", "couplekiss", "cow", "cow2", "credit_card", "crocodile", "crossed_flags", "crown", "cry", "crying_cat_face", "crystal_ball", "cupid", "curly_loop", "currency_exchange", "curry", "custard", "customs", "cyclone", "dancer", "dancers", "dango", "dart", "dash", "date", "de", "deciduous_tree", "department_store", "diamond_shape_with_a_dot_inside", "diamonds", "disappointed", "disappointed_relieved", "dizzy", "dizzy_face", "do_not_litter", "dog", "dog2", "dollar", "dolls", "dolphin", "donut", "door", "doughnut", "dragon", "dragon_face", "dress", "dromedary_camel", "droplet", "dvd", "e-mail", "ear", "ear_of_rice", "earth_africa", "earth_americas", "earth_asia", "egg", "eggplant", "eight", "eight_pointed_black_star", "eight_spoked_asterisk", "electric_plug", "elephant", "email", "end", "envelope", "es", "euro", "european_castle", "european_post_office", "evergreen_tree", "exclamation", "expressionless", "eyeglasses", "eyes", "facepunch", "factory", "fallen_leaf", "family", "fast_forward", "fax", "fearful", "feelsgood", "feet", "ferris_wheel", "file_folder", "finnadie", "fire", "fire_engine", "fireworks", "first_quarter_moon", "first_quarter_moon_with_face", "fish", "fish_cake", "fishing_pole_and_fish", "fist", "five", "flags", "flashlight", "floppy_disk", "flower_playing_cards", "flushed", "foggy", "football", "fork_and_knife", "fountain", "four", "four_leaf_clover", "fr", "free", "fried_shrimp", "fries", "frog", "frowning", "frowning_face", "fu", "fuelpump", "full_moon", "full_moon_with_face", "game_die", "gb", "gem", "gemini", "ghost", "gift", "gift_heart", "girl", "globe_with_meridians", "goat", "goberserk", "godmode", "golf", "grapes", "green_apple", "green_book", "green_heart", "grey_exclamation", "grey_question", "grimacing", "grin", "grinning", "guardsman", "guitar", "gun", "haircut", "hamburger", "hammer", "hamster", "hand", "handbag", "hankey", "hash", "hatched_chick", "hatching_chick", "headphones", "hear_no_evil", "heart", "heart_decoration", "heart_eyes", "heart_eyes_cat", "heartbeat", "heartpulse", "hearts", "heavy_check_mark", "heavy_division_sign", "heavy_dollar_sign", "heavy_exclamation_mark", "heavy_minus_sign", "heavy_multiplication_x", "heavy_plus_sign", "helicopter", "herb", "hibiscus", "high_brightness", "high_heel", "hocho", "honey_pot", "honeybee", "horse", "horse_racing", "hospital", "hotel", "hotsprings", "hourglass", "hourglass_flowing_sand", "house", "house_with_garden", "hugs", "hurtrealbad", "hushed", "ice_cream", "icecream", "id", "ideograph_advantage", "imp", "inbox_tray", "incoming_envelope", "information_desk_person", "information_source", "innocent", "interrobang", "iphone", "it", "izakaya_lantern", "jack_o_lantern", "japan", "japanese_castle", "japanese_goblin", "japanese_ogre", "jeans", "joy", "joy_cat", "jp", "key", "keycap_ten", "kimono", "kiss", "kissing", "kissing_cat", "kissing_closed_eyes", "kissing_face", "kissing_heart", "kissing_smiling_eyes", "koala", "koko", "kr", "large_blue_circle", "large_blue_diamond", "large_orange_diamond", "last_quarter_moon", "last_quarter_moon_with_face", "laughing", "leaves", "ledger", "left_luggage", "left_right_arrow", "leftwards_arrow_with_hook", "lemon", "leo", "leopard", "libra", "light_rail", "link", "lips", "lipstick", "lock", "lock_with_ink_pen", "lollipop", "loop", "loudspeaker", "love_hotel", "love_letter", "low_brightness", "m", "mag", "mag_right", "mahjong", "mailbox", "mailbox_closed", "mailbox_with_mail", "mailbox_with_no_mail", "man", "man_shrugging", "man_with_gua_pi_mao", "man_with_turban", "mans_shoe", "maple_leaf", "mask", "massage", "meat_on_bone", "mega", "melon", "memo", "mens", "metal", "metro", "microphone", "microscope", "milky_way", "minibus", "minidisc", "mobile_phone_off", "money_with_wings", "moneybag", "monkey", "monkey_face", "monorail", "moon", "mortar_board", "mount_fuji", "mountain_bicyclist", "mountain_cableway", "mountain_railway", "mouse", "mouse2", "movie_camera", "moyai", "muscle", "mushroom", "musical_keyboard", "musical_note", "musical_score", "mute", "nail_care", "name_badge", "neckbeard", "necktie", "negative_squared_cross_mark", "neutral_face", "new", "new_moon", "new_moon_with_face", "newspaper", "ng", "nine", "no_bell", "no_bicycles", "no_entry", "no_entry_sign", "no_good", "no_mobile_phones", "no_mouth", "no_pedestrians", "no_smoking", "non-potable_water", "nose", "notebook", "notebook_with_decorative_cover", "notes", "nut_and_bolt", "o", "o2", "ocean", "octocat", "octopus", "oden", "office", "ok", "ok_hand", "ok_woman", "older_man", "older_woman", "on", "oncoming_automobile", "oncoming_bus", "oncoming_police_car", "oncoming_taxi", "one", "open_file_folder", "open_hands", "open_mouth", "ophiuchus", "orange_book", "outbox_tray", "ox", "page_facing_up", "page_with_curl", "pager", "palm_tree", "panda_face", "paperclip", "parking", "part_alternation_mark", "partly_sunny", "passport_control", "paw_prints", "peach", "pear", "pencil", "pencil2", "penguin", "pensive", "performing_arts", "persevere", "person_frowning", "person_with_blond_hair", "person_with_pouting_face", "phone", "pig", "pig2", "pig_nose", "pill", "pineapple", "pisces", "pizza", "plus1", "point_down", "point_left", "point_right", "point_up", "point_up_2", "police_car", "poodle", "poop", "post_office", "postal_horn", "postbox", "potable_water", "pouch", "poultry_leg", "pound", "pouting_cat", "pray", "princess", "punch", "purple_heart", "purse", "pushpin", "put_litter_in_its_place", "question", "rabbit", "rabbit2", "racehorse", "radio", "radio_button", "rage", "rage1", "rage2", "rage3", "rage4", "railway_car", "rainbow", "raised_hand", "raised_hands", "raising_hand", "raising_hand_man", "raising_hand_woman", "ram", "ramen", "rat", "recycle", "red_car", "red_circle", "registered", "relaxed", "relieved", "repeat", "repeat_one", "restroom", "revolving_hearts", "rewind", "ribbon", "rice", "rice_ball", "rice_cracker", "rice_scene", "ring", "rocket", "roll_eyes", "face_with_rolling_eyes", "roller_coaster", "rooster", "rose", "rotating_light", "round_pushpin", "rowboat", "ru", "rugby_football", "runner", "running", "running_shirt_with_sash", "sa", "sagittarius", "sailboat", "sake", "sandal", "santa", "satellite", "satisfied", "saxophone", "school", "school_satchel", "scissors", "scorpius", "scream", "scream_cat", "scroll", "seat", "secret", "see_no_evil", "seedling", "seven", "shaved_ice", "sheep", "shell", "ship", "shipit", "shirt", "shit", "shoe", "shower", "signal_strength", "six", "six_pointed_star", "ski", "skull", "sleeping", "sleepy", "slot_machine", "small_blue_diamond", "small_orange_diamond", "small_red_triangle", "small_red_triangle_down", "smile", "simple_smile", "slightly_smiling_face", "smile_cat", "smiley", "smiley_cat", "smiling_imp", "smirk", "smirk_cat", "smoking", "snail", "snake", "snowboarder", "snowflake", "snowman", "sob", "soccer", "soon", "sos", "sound", "space_invader", "spades", "spaghetti", "sparkler", "sparkles", "sparkling_heart", "speak_no_evil", "speaker", "speech_balloon", "speedboat", "squirrel", "star", "star2", "stars", "station", "statue_of_liberty", "steam_locomotive", "stew", "straight_ruler", "strawberry", "stuck_out_tongue", "stuck_out_tongue_closed_eyes", "stuck_out_tongue_winking_eye", "sun_with_face", "sunflower", "sunglasses", "sunny", "sunrise", "sunrise_over_mountains", "surfer", "sushi", "suspect", "suspension_railway", "sweat", "sweat_drops", "sweat_smile", "sweet_potato", "swimmer", "symbols", "syringe", "tada", "tanabata_tree", "tangerine", "taurus", "taxi", "tea", "telephone", "telephone_receiver", "telescope", "tennis", "tent", "thinking", "thinking_face", "thought_balloon", "three", "thumbsdown", "thumbsup", "ticket", "tiger", "tiger2", "tired_face", "tm", "toilet", "tokyo_tower", "tomato", "tongue", "top", "tophat", "tractor", "traffic_light", "train", "train2", "tram", "triangular_flag_on_post", "triangular_ruler", "trident", "triumph", "trolleybus", "trollface", "trophy", "tropical_drink", "tropical_fish", "truck", "trumpet", "tshirt", "tulip", "turtle", "tv", "twisted_rightwards_arrows", "two", "two_hearts", "two_men_holding_hands", "two_women_holding_hands", "u5272", "u5408", "u55b6", "u6307", "u6708", "u6709", "u6e80", "u7121", "u7533", "u7981", "u7a7a", "uk", "umbrella", "unamused", "underage", "unlock", "up", "us", "v", "vertical_traffic_light", "vhs", "vibration_mode", "video_camera", "video_game", "violin", "virgo", "volcano", "vs", "walking", "waning_crescent_moon", "waning_gibbous_moon", "warning", "watch", "water_buffalo", "watermelon", "wave", "wavy_dash", "waxing_crescent_moon", "waxing_gibbous_moon", "wc", "weary", "wedding", "whale", "whale2", "wheelchair", "white_check_mark", "white_circle", "white_flower", "white_large_square", "white_square_button", "wind_chime", "wine_glass", "wink", "wolf", "woman", "woman_shrugging", "womans_clothes", "womans_hat", "womens", "worried", "wrench", "x", "yellow_heart", "yen", "yum", "zap", "zero", "zzz"];
  }

};

class ParsedDOM {

  constructor (htmlString) {
    this.body = null;

    if (this._isIe9) {
      let temp = document.createElement('div');
      temp.innerHTML = htmlString;
      this.body = temp;
    }
    else {
      var doc = new DOMParser().parseFromString(htmlString, 'text/html');
      this.body = doc.body;
    }
  }

  getTextContent (lineBreakDelimiter = " ") {
    return this._recursiveTextContent(this.body, lineBreakDelimiter).trim();
  }

  getOnlyChild () {
    return this._recursiveOnlyChild(this.body);
  }

  _recursiveTextContent (element, lineBreakDelimiter) {
    let nodes = element.childNodes;
    let text = "";
    for (let i = 0; i < nodes.length; i++) {
      let node = nodes[i];
      if (node.nodeType == 1) { // Element
        Emoji.regex.class.standard.lastIndex = 0;
        if (Emoji.regex.class.standard.test(node.className)) {
          text += node.outerHTML;
        } else if (node.nodeName == "BR") {
          text += lineBreakDelimiter;
        } else if (node.nodeName == "P") {
          text += this._recursiveTextContent(node, lineBreakDelimiter);
          text += lineBreakDelimiter;
        } else {
          text += this._recursiveTextContent(node, lineBreakDelimiter);
        }
      }
      else if (node.nodeType == 3) { // Text
        text += node.wholeText.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      }
    }
    return text;
  }

  _recursiveOnlyChild (element) {
    if (element.childNodes.length == 0) return element;
    else if (element.childNodes.length == 1) {
      if (element.childNodes[0].nodeType != 1) return element;
      else return this._recursiveOnlyChild(element.childNodes[0]);
    }
    else return null;
  }

  _isIe9 () {
    return ~navigator.appVersion.indexOf("MSIE 9.");
  }

}

const Utils = {
  _pageHostname: null,
  _pageHref:     null,

  firstDefined: array => {
    return array.filter(o => typeof o !== 'undefined')[0];
  },

  _resolvePageHostname() {
    try {
      if (window.parent && window.parent !== window) {
        return window.parent.location.hostname || '';
      }
    } catch (e) {}
    return typeof location !== 'undefined' ? (location.hostname || '') : '';
  },

  _resolvePageHref() {
    try {
      if (window.parent && window.parent !== window) {
        return window.parent.location.href;
      }
    } catch (e) {}
    return typeof location !== 'undefined' ? location.href : '';
  },

  pageHostname() {
    return this._pageHostname || this._resolvePageHostname();
  },

  pageHref() {
    return this._pageHref || this._resolvePageHref();
  },

  isSameHostname(href) {
    if (!href || /^(mailto:|tel:|#|javascript:)/i.test(href)) return true;
    try {
      const pageHostname = this.pageHostname();
      if (!pageHostname) return false;
      return new URL(href, this.pageHref()).hostname === pageHostname;
    } catch (e) {
      return false;
    }
  },

  isInIframe() {
    try {
      return typeof window !== 'undefined' && window.parent && window.parent !== window;
    } catch (e) {
      return false;
    }
  },

  linkTarget(href) {
    if (!this.isSameHostname(href)) return '_blank';
    return this.isInIframe() ? '_parent' : '_self';
  },

  linkTargetAttr(href) {
    return ' target="' + Utils.linkTarget(href) + '"';
  }
}

class ParserCache {
  constructor () {
    this.data = {};
    this._enabled = false;
    this._expire = 0;
  }

  config (data) {
    this._enabled = Utils.firstDefined([data.cacheEnabled, this._enabled]);
    this._expire = Utils.firstDefined([data.cacheExpire, this._expire]);
  }

  sync (group, text, handler) {
    if (this._enabled) {
      if (!this.data[group]) this.data[group] = {};
      if (!Object.prototype.hasOwnProperty.call(this.data[group], text)) {
        this.data[group][text] = handler();
      }
      return this.data[group][text];
    }
    else return handler();
  }
}

// See message.rb
AskTechParser.parser = {
  html: 0,
  markdown: 1,
  md: 1,
  text: 2,
}

Emoji.regex = {
  native: /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
  class: {
    standard: /(^|\s)(slaask-emoji|emoji)(?=(\s|$))/g,
    allSizes: /(^|\s)(slaask-emoji|emoji)(-big)?(?=(\s|$))/g
  }
}

window.AskTechParser = AskTechParser;

})();
